import { LiquidityAmount, Token, isEqualHex } from '@akropolis-web/primitives';
import { Observable, combineLatest, map, of, switchMap } from 'rxjs';
import * as R from 'ramda';

import { protocolsApi, ratingsApi } from 'api/modules';
import {
  AssetManipulationComplexityRating,
  MarketManipulationComplexityRatingInfo,
  NetworkSlug,
  ProductSlug,
  VersionSlug,
  isAssetManipulationComplexityRating,
  isMarketManipulationComplexityRating,
} from 'domain/types';
import { getMarketName } from 'domain/utils';
import { compareAmounts } from 'utils/amount';

export function getMarketManipulationComplexityRatingInfo({
  productSlug,
  versionSlug,
  network,
  marketId,
}: {
  productSlug: ProductSlug;
  versionSlug: VersionSlug;
  network: NetworkSlug;
  marketId: string;
}): Observable<MarketManipulationComplexityRatingInfo | null> {
  return ratingsApi.getMarketRatings$({ marketId, network, productSlug, versionSlug }).pipe(
    switchMap(ratings => {
      const rating = ratings.find(isMarketManipulationComplexityRating);

      if (!rating) return of(null);

      return combineLatest([
        of(rating),
        ratingsApi.getAssetsRatings$({ network, productSlug, versionSlug, marketId }),
        protocolsApi.getLendingInfo$(true, { network, productSlug, versionSlug }),
      ]);
    }),
    map((data): MarketManipulationComplexityRatingInfo | null => {
      if (!data) {
        return null;
      }

      const [rating, assetsRatings, protocolInfo] = data;

      const worstAssetRatings = Array.from(assetsRatings.values())
        .filter(isAssetManipulationComplexityRating)
        .filter(
          assetRating => assetRating.rating === rating.rating && !!getImpactCost(assetRating),
        );

      if (!worstAssetRatings.length) {
        return {
          rating,
          valueAtRisk: getValueAtRisk(rating),
          impactCost: getImpactCost(rating),
          transcription: {
            marketName: getMarketName(protocolInfo, marketId),
            badAssets: [],
          },
        };
      }

      const allLendingTokens = protocolInfo.markets.map(market => market.tokens).flat();

      const worstRating = [...worstAssetRatings].sort((a, b) =>
        compareAmounts(getImpactCost(b), getImpactCost(a)),
      )[0];

      const worstTokenAddresses = R.uniq(worstAssetRatings.map(({ tokenAddress }) => tokenAddress));
      const worstTokens = worstTokenAddresses
        .map(tokenAddress =>
          allLendingTokens.find(token => isEqualHex(token.address, tokenAddress)),
        )
        .filter((value: Token | undefined): value is Token => !!value);

      return {
        rating,
        valueAtRisk: getValueAtRisk(worstRating),
        impactCost: getImpactCost(worstRating),
        transcription: {
          marketName: getMarketName(protocolInfo, marketId),
          badAssets: worstTokens,
        },
      };
    }),
  );
}

function getValueAtRisk(
  rating: Pick<AssetManipulationComplexityRating, 'payload' | 'rating'>,
): LiquidityAmount | null {
  return rating.rating <= 1 || rating.payload.pumpRating >= rating.payload.dumpRating
    ? rating.payload.pumpRiskTvl
    : rating.payload.dumpRiskTvl;
}

function getImpactCost(
  rating: Pick<AssetManipulationComplexityRating, 'payload' | 'rating'>,
): LiquidityAmount | null {
  return rating.rating <= 1 || rating.payload.pumpRating >= rating.payload.dumpRating
    ? rating.payload.pumpCost
    : rating.payload.dumpCost;
}
